import '../jobseeker-v2/workspaceKitTheme.css';
import './counselorLogin.css';
import BrandLogo from '../brand/BrandLogo';

const AUTH0_ISSUE_URL = 'https://linear.app/oregonaix/issue/RSW-76';

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <rect x="4" y="3" width="16" height="18" rx="1" />
      <path d="M9 8h.01M15 8h.01M9 12h.01M15 12h.01M9 16h6" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/**
 * Kit-styled counselor login (RSW-291 / CPW-1.3).
 * Split layout: navy brand/trust panel + secure login card. Retires the blue
 * Chakra default. When the Auth0 bridge is enabled (RSW-301) an active SSO
 * button is injected via `ssoSlot`; otherwise a disabled placeholder shows until
 * the tenant lands (RSW-76). The dev bcrypt fallback only renders under DEV.
 */
export default function CounselorLogin({
  email,
  password,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  loading = false,
  error = '',
  isDev = false,
  devEmail = '',
  devPassword = '',
  onDevAutofill,
  ssoSlot = null,
}) {
  // Auth0-first (RSW-758): migrated counselors have no local password, so the
  // email/password form is hidden outside DEV once the SSO slot is active.
  const ssoPrimary = Boolean(ssoSlot);

  const passwordForm = (
    <>
      <div className="csl-login__field">
        <label className="csl-login__label" htmlFor="csl-login-email">Email</label>
        <input
          id="csl-login-email"
          className="csl-login__input"
          type="email"
          autoComplete="username"
          required
          value={email}
          placeholder="you@organization.org"
          onChange={(e) => onEmailChange(e.target.value)}
        />
      </div>

      <div className="csl-login__field">
        <label className="csl-login__label" htmlFor="csl-login-password">Password</label>
        <input
          id="csl-login-password"
          className="csl-login__input"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          placeholder="Enter your password"
          onChange={(e) => onPasswordChange(e.target.value)}
        />
      </div>

      <button type="submit" className="csl-login__submit" disabled={loading}>
        {loading ? 'Signing in…' : 'Sign in'}
      </button>
    </>
  );

  const devFallback = isDev ? (
    <div className="csl-login__dev">
      <p className="csl-login__dev-head">Development only</p>
      <p className="csl-login__dev-row">{devEmail}</p>
      <p className="csl-login__dev-row">{devPassword}</p>
      <button
        type="button"
        className="csl-login__dev-btn"
        onClick={onDevAutofill}
        disabled={loading}
      >
        Fill email and password
      </button>
    </div>
  ) : null;

  return (
    <div className="csl-login ws-kit-theme">
      <div className="csl-login__brand">
        <BrandLogo theme="dark" className="csl-login__logo" />
        <p className="csl-login__eyebrow">Workforce partner platform</p>
        <h2 className="csl-login__headline">Counselor sign in</h2>
        <p className="csl-login__lede">
          Manage your caseload, send secure access links, and follow each jobseeker&rsquo;s
          progress from one workspace.
        </p>
        <ul className="csl-login__trust">
          <li><BuildingIcon /><span>Access is scoped to your organization&rsquo;s caseload.</span></li>
          <li><ShieldIcon /><span>Encrypted sessions; we never share jobseeker data across partners.</span></li>
          <li><ClockIcon /><span>Sessions time out after inactivity to protect participant privacy.</span></li>
        </ul>
        <p className="csl-login__brand-foot">
          Jobseekers don&rsquo;t sign in here. They use the secure access link you send them.
        </p>
      </div>

      <form className="csl-login__form" onSubmit={onSubmit}>
        <div>
          <h3 className="csl-login__form-title">Sign in to your workspace</h3>
          <p className="csl-login__form-sub">
            {ssoPrimary
              ? 'Enter your work email on the next screen and we\u2019ll send you a one-time sign-in code. No password needed.'
              : 'Use your counselor email and password.'}
          </p>
        </div>

        {error ? <div className="csl-login__error" role="alert">{error}</div> : null}

        {ssoSlot || (
          <button
            type="button"
            className="csl-login__auth0"
            disabled
            aria-disabled="true"
            title={`Single sign-on is coming soon (${AUTH0_ISSUE_URL})`}
          >
            <ShieldIcon />
            Continue with single sign-on
            <span className="csl-login__auth0-tag">Coming soon</span>
          </button>
        )}

        {ssoPrimary ? (
          <>
            <p className="csl-login__sso-help">
              Use the email your organization has on file with RS Works.
            </p>
            {isDev ? (
              <details className="csl-login__dev-details">
                <summary>Development password sign-in</summary>
                {passwordForm}
                {devFallback}
              </details>
            ) : null}
          </>
        ) : (
          <>
            <div className="csl-login__divider">or</div>
            {passwordForm}
            {devFallback}
          </>
        )}

        <p className="csl-login__foot">
          Save changes regularly once signed in. Your secure session may time out after inactivity.
        </p>
      </form>
    </div>
  );
}
